import { useEffect, useRef } from "react";
import { loadGoogleMaps } from "@/lib/google-maps-loader";

interface Props {
  lat: number;
  lng: number;
  heading?: number;
}

export const StreetView = ({ lat, lng, heading }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const panoRef = useRef<any>(null);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps().then((g) => {
      if (cancelled || !containerRef.current) return;
      const position = { lat, lng };
      const svService = new g.maps.StreetViewService();
      // Find the nearest panorama and orient it toward the building.
      svService.getPanorama(
        { location: position, radius: 80, source: g.maps.StreetViewSource.OUTDOOR },
        (data: any, status: any) => {
          if (cancelled || !containerRef.current) return;
          if (status !== "OK" || !data?.location?.latLng) {
            // Fallback: try default service without source filter
            svService.getPanorama({ location: position, radius: 150 }, (d2: any, s2: any) => {
              if (cancelled || !containerRef.current) return;
              if (s2 !== "OK" || !d2?.location?.latLng) return;
              renderPano(g, d2);
            });
            return;
          }
          renderPano(g, data);
        },
      );

      const renderPano = (gg: any, data: any) => {
        const panoLatLng = data.location.latLng;
        const computedHeading =
          heading ??
          gg.maps.geometry?.spherical?.computeHeading?.(panoLatLng, new gg.maps.LatLng(lat, lng)) ??
          0;
        const opts = {
          pano: data.location.pano,
          pov: { heading: computedHeading, pitch: 5 },
          zoom: 0.6,
          addressControl: false,
          fullscreenControl: false,
          motionTracking: false,
          motionTrackingControl: false,
          panControl: false,
          zoomControl: true,
          linksControl: false,
          showRoadLabels: false,
        };
        if (panoRef.current) {
          panoRef.current.setOptions(opts);
        } else {
          panoRef.current = new gg.maps.StreetViewPanorama(containerRef.current, opts);
        }
      };
    });
    return () => {
      cancelled = true;
    };
  }, [lat, lng, heading]);

  return (
    <div
      ref={containerRef}
      className="w-full h-64 sm:h-72 rounded-lg overflow-hidden border border-border bg-muted"
      aria-label="Vista de calle de la dirección"
    />
  );
};
